import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

function DisciplineSelector({ user, isAdmin }) {
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDisciplineName, setNewDisciplineName] = useState('');
  const [addingDiscipline, setAddingDiscipline] = useState(false);
  const [addError, setAddError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isOwner = user && user.email === 'phitaylr@gmail.com';
  const accessDeniedDiscipline = searchParams.get('accessDenied');

  useEffect(() => {
    fetchDisciplines();
  }, []);

  const fetchDisciplines = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/outcomes/api/disciplines', { withCredentials: true });
      setDisciplines(response.data);
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setError('Could not load available disciplines');
      // Default to stem if error
      setDisciplines(['stem']);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDiscipline = async (e) => {
    e.preventDefault();
    setAddError('');

    if (!newDisciplineName.trim()) {
      setAddError('Discipline name is required');
      return;
    }

    setAddingDiscipline(true);
    try {
      const response = await axios.post('/outcomes/api/disciplines', {
        name: newDisciplineName.trim()
      });

      setNewDisciplineName('');
      setShowAddForm(false);
      await fetchDisciplines();
      
      // Navigate to the new discipline admin page
      navigate(`/admin/${response.data.discipline}`);
    } catch (err) {
      console.error('Error adding discipline:', err);
      setAddError(err.response?.data?.error || 'Failed to create discipline');
    } finally {
      setAddingDiscipline(false);
    }
  };

  if (loading) {
    return (
      <div className="discipline-selector">
        <div className="loading">Loading disciplines...</div>
      </div>
    );
  }

  const basePath = isAdmin ? '/admin' : '';

  return (
    <div className="discipline-selector-page">
      <div className="discipline-container">
        <h2>{isAdmin ? 'Select a Discipline to Manage' : 'Select a Discipline'}</h2>
        
        {accessDeniedDiscipline && (
          <div className="error-message">
            {user ? (
              <>
                <strong>Access Denied:</strong> The <strong>{accessDeniedDiscipline}</strong> discipline requires admin access. 
                {!user.is_admin && ' Please contact an administrator.'}
              </>
            ) : (
              <>
                <strong>Login Required:</strong> The <strong>{accessDeniedDiscipline}</strong> discipline requires admin access.
                <div style={{ marginTop: '12px' }}>
                  <a href="https://rstem.net/outcomes/auth/google" className="google-login-btn" style={{ display: 'inline-block' }}>
                    Sign in with Google
                  </a>
                </div>
              </>
            )}
          </div>
        )}
        
        {error && <div className="error-message">{error}</div>}

        {disciplines.length === 0 ? (
          <div className="no-disciplines">
            <p>No disciplines available. Contact an administrator.</p>
          </div>
        ) : (
          <div className="disciplines-grid">
            {disciplines.map((discipline) => (
              <Link
                key={discipline}
                to={`${basePath}/${discipline}`}
                className="discipline-card"
              >
                <div className="discipline-name">
                  {discipline.charAt(0).toUpperCase() + discipline.slice(1)}
                </div>
                <div className="discipline-arrow">→</div>
              </Link>
            ))}
          </div>
        )}

        {isOwner && isAdmin && (
          <div className="add-discipline-section">
            {!showAddForm ? (
              <button
                className="add-discipline-btn"
                onClick={() => setShowAddForm(true)}
              >
                + Add New Discipline
              </button>
            ) : (
              <form onSubmit={handleAddDiscipline} className="add-discipline-form">
                <div className="form-group">
                  <label htmlFor="disciplineName">Discipline Name</label>
                  <input
                    id="disciplineName"
                    type="text"
                    value={newDisciplineName}
                    onChange={(e) => setNewDisciplineName(e.target.value)}
                    placeholder="e.g., physics, biology, engineering"
                    disabled={addingDiscipline}
                    autoFocus
                  />
                </div>
                {addError && <div className="error-message">{addError}</div>}
                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewDisciplineName('');
                      setAddError('');
                    }}
                    disabled={addingDiscipline}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={addingDiscipline}
                  >
                    {addingDiscipline ? 'Creating...' : 'Create Discipline'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      <style>{`
        .discipline-selector-page {
          min-height: calc(100vh - 120px);
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          padding: 20px;
        }

        .discipline-container {
          background: white;
          border-radius: 8px;
          padding: 40px;
          max-width: 600px;
          width: 100%;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .discipline-container h2 {
          margin-top: 0;
          margin-bottom: 30px;
          text-align: center;
          color: #333;
        }

        .disciplines-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 15px;
        }

        .discipline-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          background: #f9f9f9;
          border: 2px solid #e0e0e0;
          border-radius: 6px;
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .discipline-card:hover {
          background: #ebebeb;
          border-color: #2196F3;
          transform: translateX(5px);
        }

        .discipline-name {
          font-size: 18px;
          font-weight: 500;
          color: #333;
        }

        .discipline-arrow {
          font-size: 24px;
          color: #2196F3;
          transition: color 0.2s ease;
        }

        .discipline-card:hover .discipline-arrow {
          color: #1976D2;
        }

        .loading, .no-disciplines {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .error-message {
          background-color: #ffebee;
          color: #c62828;
          padding: 12px 16px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .add-discipline-section {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 1px solid #e0e0e0;
        }

        .add-discipline-btn {
          display: block;
          width: 100%;
          padding: 12px 20px;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .add-discipline-btn:hover {
          background: #45a049;
        }

        .add-discipline-form {
          background: #f0f7ff;
          padding: 20px;
          border-radius: 6px;
          border: 1px solid #bbdefb;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #333;
        }

        .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
        }

        .form-group input:focus {
          outline: none;
          border-color: #2196F3;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 16px;
        }

        .cancel-btn, .submit-btn {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .cancel-btn {
          background: #e0e0e0;
          color: #333;
        }

        .cancel-btn:hover {
          background: #d0d0d0;
        }

        .submit-btn {
          background: #2196F3;
          color: white;
        }

        .submit-btn:hover {
          background: #1976D2;
        }

        .cancel-btn:disabled, .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

export default DisciplineSelector;
